"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import EnquiryForm from "./EnquiryForm";
import { UnitInterest } from "@/interfaces/propertyinterface";

interface EnquiryModalProps {
  open: boolean;
  onClose: () => void;
  propertyId?: string;
  unitInterest?: UnitInterest;
  title?: string;
}

/**
 * Shared enquiry modal — wraps EnquiryForm with the standard chrome
 * (backdrop, header, close button, scroll). Use everywhere a popup is
 * needed: property page, price-list unit popup, homepage auto-popup.
 */
const EnquiryModal = ({
  open,
  onClose,
  propertyId,
  unitInterest,
  title = "Find out more about this project",
}: EnquiryModalProps) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4 custom-scrollbar">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close enquiry modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <EnquiryForm
          onClose={onClose}
          propertyId={propertyId}
          unitInterest={unitInterest}
        />
      </div>
    </div>
  );
};

interface AutoEnquiryModalProps {
  /** Delay before auto-opening, in ms. Default 5000 (5s). */
  delayMs?: number;
  /** Local-storage key to remember "user dismissed" between page loads. */
  dismissKey?: string;
  propertyId?: string;
  unitInterest?: UnitInterest;
  title?: string;
}

/**
 * Auto-popup variant — opens itself after `delayMs`. Won't reopen
 * once dismissed (per page load, or persistently if dismissKey set).
 */
export const AutoEnquiryModal = ({
  delayMs = 5000,
  dismissKey,
  propertyId,
  unitInterest,
  title,
}: AutoEnquiryModalProps) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissKey && typeof window !== "undefined") {
      if (window.sessionStorage.getItem(dismissKey)) {
        setDismissed(true);
        return;
      }
    }
    const t = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs, dismissKey]);

  const handleClose = () => {
    setOpen(false);
    setDismissed(true);
    if (dismissKey && typeof window !== "undefined") {
      window.sessionStorage.setItem(dismissKey, "1");
    }
  };

  if (dismissed && !open) return null;
  return (
    <EnquiryModal
      open={open}
      onClose={handleClose}
      propertyId={propertyId}
      unitInterest={unitInterest}
      title={title}
    />
  );
};

export default EnquiryModal;
